/**
 * Portfolio Context for managing selected portfolio across the application.
 * 
 * Provides:
 * - Selected portfolio ID
 * - Portfolio metadata
 * - Methods to switch portfolios
 * - Loading state
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { portfoliosApi } from '../services/api';
import type { PortfolioMetadata } from '../types';

interface PortfolioContextType {
  selectedPortfolioId: string | null;
  portfolios: PortfolioMetadata[];
  selectedPortfolio: PortfolioMetadata | null;
  isLoading: boolean;
  error: string | null;
  setSelectedPortfolioId: (portfolioId: string) => void;
  refreshPortfolios: () => Promise<void>;
}

const PortfolioContext = createContext<PortfolioContextType | undefined>(undefined);

interface PortfolioProviderProps {
  children: ReactNode;
}

export function PortfolioProvider({ children }: PortfolioProviderProps) {
  const [selectedPortfolioId, setSelectedPortfolioIdState] = useState<string | null>(null);
  const [portfolios, setPortfolios] = useState<PortfolioMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPortfolios = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await portfoliosApi.list();
      setPortfolios(response.portfolios);

      // If no portfolio is selected, select the primary one
      if (!selectedPortfolioId && response.portfolios.length > 0) {
        const primary = response.portfolios.find((p) => p.isPrimary);
        if (primary) {
          setSelectedPortfolioIdState(primary.portfolioId);
        } else {
          // Fallback to first portfolio if no primary found
          setSelectedPortfolioIdState(response.portfolios[0].portfolioId);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load portfolios');
      console.error('Failed to load portfolios:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPortfolios();
  }, []);

  const setSelectedPortfolioId = (portfolioId: string) => {
    setSelectedPortfolioIdState(portfolioId);
    // Store in localStorage for persistence
    localStorage.setItem('selectedPortfolioId', portfolioId);
  };

  // Load selected portfolio from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('selectedPortfolioId');
    if (stored && portfolios.some((p) => p.portfolioId === stored)) {
      setSelectedPortfolioIdState(stored);
    }
  }, [portfolios]);

  const selectedPortfolio = portfolios.find((p) => p.portfolioId === selectedPortfolioId) || null;

  const value: PortfolioContextType = {
    selectedPortfolioId,
    portfolios,
    selectedPortfolio,
    isLoading,
    error,
    setSelectedPortfolioId,
    refreshPortfolios: loadPortfolios,
  };

  return <PortfolioContext.Provider value={value}>{children}</PortfolioContext.Provider>;
}

export function usePortfolio() {
  const context = useContext(PortfolioContext);
  if (context === undefined) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
}

